import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

// Use XMLHttpRequest to bypass any global fetch wrappers (Emergent debug-monitor,
// PostHog session recording, etc.) that consume the response body and cause
// "body stream already read" errors when we try to parse the JSON ourselves.
function request(method, path, body, token) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, `${API}${path}`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = () => {
      const text = xhr.responseText || '';
      let data = {};
      if (text) {
        try { data = JSON.parse(text); } catch { data = { detail: text }; }
      }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(body ? JSON.stringify(body) : null);
  });
}

function detailToMessage(detail, fallback) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return fallback;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('selora_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('selora_token');
  }, []);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    request('GET', '/api/auth/me', null, token)
      .then(({ ok, data }) => {
        if (!ok) throw new Error();
        setUser(data);
        setLoading(false);
      })
      .catch(() => { logout(); setLoading(false); });
  }, [token, logout]);

  const parseResponse = ({ ok, data }, fallbackMsg) => {
    if (!ok) throw new Error(detailToMessage(data.detail, fallbackMsg));
    return data;
  };

  const login = async (email, password) => {
    const res = await request('POST', '/api/auth/login', { email, password });
    const data = parseResponse(res, 'Login failed');
    localStorage.setItem('selora_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const signup = async (full_name, email, password) => {
    const res = await request('POST', '/api/auth/signup', { full_name, email, password });
    const data = parseResponse(res, 'Signup failed');
    localStorage.setItem('selora_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  // Called by AuthCallback after Emergent Google Auth returns a session_id.
  const googleCallback = async (session_id) => {
    const res = await request('POST', '/api/auth/google/callback', { session_id });
    const data = parseResponse(res, 'Google sign-in failed');
    localStorage.setItem('selora_token', data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const forgotPassword = async (email) => {
    const res = await request('POST', '/api/auth/forgot-password', { email });
    return parseResponse(res, 'Request failed');
  };

  const resetPassword = async (token, new_password) => {
    const res = await request('POST', '/api/auth/reset-password', { token, new_password });
    return parseResponse(res, 'Reset failed');
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, logout, forgotPassword, resetPassword, googleCallback }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
